import styled from 'styled-components';

import { FormError } from '@components/form-error';
import { FormDataType, FormInstance } from '@components/form/create-form';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { getScopedTranslate, useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { durationHoursList, durationMinutesList } from '@utils/number-arrays';

const HOURS_PER_DAY = 24;
const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = HOURS_PER_DAY * MINUTES_PER_HOUR;
// timedelta's own ceiling, and the point past which String(Math.floor(n)) would
// switch to exponential notation — a "1e+21" days value parses as ValueError on
// the backend, which is caught, so the traveller would stall with nothing said.
const MAX_MINUTES = 999999999 * MINUTES_PER_DAY;

export const WAIT_FOR_MINIMUM_MINUTES = 1;
export const LOOP_MINIMUM_MINUTES = 10;

type DurationParts = {
  days?: string;
  hours?: string;
  minutes?: string;
};

export const getWaitForTimeLength = (parts: DurationParts) => {
  const { t } = getScopedTranslate(locale);
  const normalized = normalizeDuration(parts);
  const dayCount = Number(normalized.days);
  const hourCount = Number(normalized.hours);
  const minuteCount = Number(normalized.minutes);
  const timeParts = [];
  if (dayCount) {
    timeParts.push(dayCount === 1 ? t('nrDay') : t('nrDays', { nr: dayCount }));
  }
  if (hourCount) {
    timeParts.push(hourCount === 1 ? t('nrHour') : t('nrHours', { nr: hourCount }));
  }
  if (minuteCount) {
    timeParts.push(minuteCount === 1 ? t('nrMinute') : t('nrMinutes', { nr: minuteCount }));
  }
  return timeParts.join(', ');
};

export const getDurationMinutes = ({ days, hours, minutes }: DurationParts) =>
  Math.min(
    toCount(days) * MINUTES_PER_DAY + toCount(hours) * MINUTES_PER_HOUR + toCount(minutes),
    MAX_MINUTES
  );

// Actions saved before the pickers were capped can hold 24 hours or 60 minutes.
// Carrying them into the unit above keeps the wait exactly as long while giving
// every picker a value it can show and every card one spelling of it.
export const normalizeDuration = (parts: DurationParts): Required<DurationParts> => {
  const totalMinutes = getDurationMinutes(parts);
  return {
    days: String(Math.floor(totalMinutes / MINUTES_PER_DAY)),
    hours: String(Math.floor(totalMinutes / MINUTES_PER_HOUR) % HOURS_PER_DAY),
    minutes: String(totalMinutes % MINUTES_PER_HOUR),
  };
};

// Both save paths go through here: the footer button and the unsaved-changes
// prompt, which submits straight through the form store and never sees the
// footer's disabled state.
export const toDurationValues = (parts: DurationParts, minimumMinutes: number) => {
  const duration = normalizeDuration(parts);
  if (getDurationMinutes(duration) < minimumMinutes) return null;

  return {
    days: { value: duration.days },
    hours: { value: duration.hours },
    minutes: { value: duration.minutes },
  };
};

const toCount = (value?: string) => {
  const count = Number(value);
  return Number.isFinite(count) ? count : 0;
};

export const FormGroupContainer = styled(Stack).attrs({
  space: 2,
  orientation: 'horizontal',
})({
  flexDirection: 'column',
  '> *': {
    flex: '1 1',
  },
  [theme.mediaBreakpoint.md]: {
    flexDirection: 'row',
  },
});

// `unknown` would accept any field type and so prove nothing; the union is what
// rejects a wrongly-typed form. TimeSavedData's parts are numbers, not strings.
type DurationFormData = {
  days: string | number;
  hours: string | number;
  minutes: string | number;
};

type DurationFieldsProps<T> = {
  form: FormInstance<T>;
  defaults: Required<DurationParts>;
};

export const DurationFields = <T extends FormDataType & DurationFormData>({
  form,
  defaults,
}: DurationFieldsProps<T>) => {
  const { t } = useScopedTranslate(locale);
  // `id` resolves through KeysOfValueType, which TypeScript cannot evaluate
  // against an unresolved generic — the prop constraint already proved the keys.
  const Form = form as FormInstance<Required<DurationParts>>;

  return (
    <FormGroupContainer>
      <Form.TextField
        id="days"
        label={t('days')}
        defaultValue={defaults.days}
        validators={['number']}
      />
      <Form.Select
        id="hours"
        label={t('hours')}
        options={durationHoursList}
        placeholder=""
        defaultValue={defaults.hours}
        isClearable={false}
      />
      <Form.Select
        id="minutes"
        label={t('minutes')}
        options={durationMinutesList}
        placeholder=""
        defaultValue={defaults.minutes}
        isClearable={false}
      />
    </FormGroupContainer>
  );
};

// States whatever positive duration was entered; whether that duration is
// *allowed* is the sidebar's floor, which can be higher. A negative entry is not
// a wait at all, so it gets nothing rather than "-1 days".
export const DurationSummary = ({
  variant,
  parts,
}: {
  variant: 'wait' | 'loop';
  parts: DurationParts;
}) => {
  const { styledTranslate } = useScopedTranslate(locale);
  const timeLength = getWaitForTimeLength(parts);

  if (getDurationMinutes(parts) < 1) return null;

  return (
    <Text font="regular14">
      {styledTranslate(variant === 'wait' ? 'waitSummary' : 'loopSummary', {
        timeLength: {
          text: timeLength,
          color: 'elementBlue100',
          font: 'regular14',
          style: { display: 'inline' },
        },
      })}
    </Text>
  );
};

// A spec in helper tone until the duration itself is edited, a real error after
// — colour alone would leave the change invisible to anyone who cannot see it.
export const DurationRequirement = ({
  variant,
  isDirty,
}: {
  variant: 'wait' | 'loop';
  isDirty: boolean;
}) => {
  const { t } = useScopedTranslate(locale);
  const text = variant === 'wait' ? t('waitMinimum') : t('loopMinimum');

  return isDirty ? (
    <FormError text={text} isAnnounced />
  ) : (
    <Text font="regular14" color="textHelper" text={text} />
  );
};

export const DurationBreakdown = ({ parts }: { parts: DurationParts }) => {
  const { t } = useScopedTranslate(locale);
  const { days, hours, minutes } = normalizeDuration(parts);

  return (
    <>
      {days !== '0' && (
        <Text
          font="semibold14"
          text={days === '1' ? t('dayConfig') : t('daysConfig', { total: days })}
        />
      )}
      {hours !== '0' && (
        <Text
          font="semibold14"
          text={hours === '1' ? t('hourConfig') : t('hoursConfig', { total: hours })}
        />
      )}
      {minutes !== '0' && (
        <Text
          font="semibold14"
          text={minutes === '1' ? t('minuteConfig') : t('minutesConfig', { total: minutes })}
        />
      )}
    </>
  );
};

const locale = {
  en: {
    days: 'Days',
    hours: 'Hours',
    minutes: 'Minutes',
    nrDay: '1 day',
    nrDays: '{nr} days',
    nrHour: '1 hour',
    nrHours: '{nr} hours',
    nrMinute: '1 minute',
    nrMinutes: '{nr} minutes',
    waitMinimum: 'Enter a duration of at least 1 minute',
    loopMinimum: 'Loop cannot run more frequently than every 10 minutes',
    waitSummary: 'Waits for {timeLength} before triggering the following action',
    loopSummary: 'Restarts loop every {timeLength}',
    dayConfig: '1 • Day',
    daysConfig: '{total} • Days',
    hourConfig: '1 • Hour',
    hoursConfig: '{total} • Hours',
    minuteConfig: '1 • Minute',
    minutesConfig: '{total} • Minutes',
  },
};
